import { apiGet, apiPost } from './apiClient.js';

export const LAUNCH_TRIAL_DURATION_DAYS = 15;
export const DEMO_TENANT_SLUG = 'demo';

export interface DemoStatus {
  readonly available: boolean;
  readonly slug: 'demo';
  readonly label: string;
}

export interface TrialOtpChallenge {
  readonly challengeId: string;
  readonly expiresInSeconds: number;
}

export interface TrialOtpProof {
  readonly otpProof: string;
}

export interface TrialSignupResult {
  readonly slug: string;
  readonly organizationName: string;
  readonly trialDurationDays: number;
  readonly loginPath: string;
}

export async function getDemoStatus(): Promise<DemoStatus> {
  return apiGet<DemoStatus>('/public/api/v1/demo');
}

export interface SlugAvailability {
  readonly available: boolean;
}

export async function checkSlugAvailability(slug: string): Promise<SlugAvailability> {
  return apiGet<SlugAvailability>(
    `/public/api/v1/slug-availability?slug=${encodeURIComponent(slug)}`,
  );
}

export async function requestPublicTrialOtp(
  ownerLoginIdentifier: string,
): Promise<TrialOtpChallenge> {
  return apiPost<TrialOtpChallenge>('/public/api/v1/otp/challenges', { ownerLoginIdentifier });
}

export async function verifyPublicTrialOtp(input: {
  ownerLoginIdentifier: string;
  challengeId: string;
  otp: string;
}): Promise<TrialOtpProof> {
  return apiPost<TrialOtpProof>('/public/api/v1/otp/verifications', input);
}

/**
 * Onboarding Desktop parity (design §A8) — mirrors
 * `apps/api/src/onboarding/trial-signup.ts`'s `ALLOWED_INDUSTRIES` exactly.
 * Duplicated rather than imported: apps/client and apps/api are separate
 * deployables (same pattern as `LAUNCH_TRIAL_DURATION_DAYS` above).
 */
export const INDUSTRY_OPTIONS = [
  { code: 'retail', label: 'خرده‌فروشی' },
  { code: 'wholesale', label: 'عمده‌فروشی' },
  { code: 'manufacturing', label: 'تولید و ساخت' },
  { code: 'food_hospitality', label: 'غذا و هتلداری' },
  { code: 'construction', label: 'ساخت‌وساز' },
  { code: 'services', label: 'خدمات' },
  { code: 'technology', label: 'فناوری اطلاعات' },
  { code: 'healthcare', label: 'سلامت و درمان' },
  { code: 'education', label: 'آموزش' },
  { code: 'other', label: 'سایر' },
] as const;
export type IndustryCode = (typeof INDUSTRY_OPTIONS)[number]['code'];

/** Mirrors `ALLOWED_TEAM_SIZES` in the same backend file. */
export const TEAM_SIZE_OPTIONS = [
  { code: '1-5', label: '۱ تا ۵ نفر' },
  { code: '6-20', label: '۶ تا ۲۰ نفر' },
  { code: '21-50', label: '۲۱ تا ۵۰ نفر' },
  { code: '51-200', label: '۵۱ تا ۲۰۰ نفر' },
  { code: '200+', label: 'بیش از ۲۰۰ نفر' },
] as const;
export type TeamSizeCode = (typeof TEAM_SIZE_OPTIONS)[number]['code'];

/** Mirrors `ALLOWED_LOGO_MIME_TYPES`/`LOGO_MAX_BYTES` in the same backend file (design §A6). */
export const LOGO_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'image/webp',
] as const;
export const LOGO_MAX_BYTES = 2_000_000;

export async function startPublicTrial(
  input: {
    organizationName: string;
    slug: string;
    ownerFirstName: string;
    ownerLastName: string;
    ownerLoginIdentifier: string;
    ownerPassword: string;
    ownerPasswordConfirmation: string;
    otpProof: string;
    /**
     * Onboarding Desktop parity (§A5/§A6/§A8) — all optional. §A6's logo
     * upload has no field here: no object-storage subsystem exists yet to
     * receive it (a separate decision, not a client gap).
     */
    brandName?: string;
    ownerRole?: string;
    /** Gregorian `YYYY-MM-DD` — the `JalaliDateInput` display-boundary wire format, never Jalali (AGENTS.md §9). */
    ownerBirthdate?: string;
    industries?: readonly IndustryCode[];
    teamSizeRange?: TeamSizeCode;
    /** Design §A6 — a `data:<mime>;base64,...` URL, as `FileReader.readAsDataURL()` produces. */
    logoDataUrl?: string;
  },
  idempotencyKey: string,
): Promise<TrialSignupResult> {
  return apiPost<TrialSignupResult>('/public/api/v1/trial', input, {
    headers: { 'idempotency-key': idempotencyKey },
  });
}

export type PricingInterval = '1_month' | '3_months' | '6_months' | '12_months';

export interface PublicPlan {
  readonly planId: string;
  readonly name: string;
  readonly billingInterval: string;
  readonly basePriceRial: string;
  readonly seatIncluded: number;
}

/**
 * Mirrors the real backend's `CreateCheckoutInput`
 * (`apps/api/src/onboarding/checkout.service.ts`) — accepts either a
 * `planId` (fixed-plan checkout) or a `usersCount`/`interval` pair (the
 * dynamic pricing-calculator path `start/CheckoutPage.tsx` and
 * `admin/SubscriptionPage.tsx` actually use); the server defaults to a
 * 10-user/12-month plan if neither is given. Fixed 2026-09-20
 * (design-agent re-audit): this type previously required `planId` and had
 * no `usersCount`/`interval`/`isUpgrade` fields at all, forcing
 * `CheckoutPage.tsx` to bypass the type system with an `as unknown as`
 * cast to call this function — a real type-safety gap, not a design
 * finding, fixed alongside the design-compliance sweep that found it.
 */
export interface CreateCheckoutInput {
  readonly planId?: string;
  readonly usersCount?: number;
  readonly interval?: PricingInterval;
  readonly organizationName: string;
  readonly slug: string;
  readonly ownerLoginIdentifier: string;
  readonly ownerFirstName: string;
  readonly ownerLastName: string;
  readonly ownerPassword: string;
  readonly isUpgrade?: boolean;
}

export interface CreateCheckoutResponse {
  readonly orderId: string;
  readonly authority: string;
  readonly paymentUrl: string;
  readonly amountRial: string;
}

export async function listPublicPlans(): Promise<{ plans: readonly PublicPlan[] }> {
  return apiGet<{ plans: readonly PublicPlan[] }>('/public/api/v1/plans');
}

export interface PlanPriceCalculation {
  readonly usersCount: number;
  readonly baseSeats: number;
  readonly extraSeats: number;
  readonly interval: string;
  readonly intervalLabel: string;
  readonly months: number;
  readonly monthlyBaseRial: string;
  readonly monthlyExtraRial: string;
  readonly monthlyTotalRial: string;
  readonly rawTotalRial: string;
  readonly discountPercent: number;
  readonly discountRial: string;
  readonly finalTotalRial: string;
  readonly finalTotalToman: string;
  readonly effectiveMonthlyToman: string;
}

export async function calculatePublicPlanPrice(
  users: number,
  interval: string,
): Promise<PlanPriceCalculation> {
  return apiGet<PlanPriceCalculation>(
    `/public/api/v1/plans/calculate?users=${encodeURIComponent(users)}&interval=${encodeURIComponent(interval)}`,
  );
}

export async function createPublicCheckout(
  input: CreateCheckoutInput,
  idempotencyKey: string,
): Promise<CreateCheckoutResponse> {
  return apiPost<CreateCheckoutResponse>('/public/api/v1/checkout/create', input, {
    headers: { 'idempotency-key': idempotencyKey },
  });
}

export async function upgradeTenantSubscription(
  slug: string,
  input: { usersCount: number; interval: string },
): Promise<CreateCheckoutResponse> {
  return apiPost<CreateCheckoutResponse>(`/t/${slug}/api/v1/admin/subscription/upgrade`, input);
}
